import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { currency, resolveImage } from "@/lib/api";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#f9f6f0] border-l border-[#e6e2d8] flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader>
          <SheetTitle className="font-serif text-3xl text-[#1e392a]">Your Cart</SheetTitle>
          <p className="text-xs text-[#8a8d86] overline">
            {items.length} {items.length === 1 ? "item" : "items"}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-20">
              <ShoppingBag
                className="h-10 w-10 mx-auto text-[#8a8d86] mb-4"
                strokeWidth={1.5}
              />
              <p className="text-sm text-[#8a8d86]">Your cart is empty.</p>
              <Button
                data-testid="cart-empty-shop-btn"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/shop");
                }}
                className="mt-6 bg-[#1e392a] hover:bg-[#14251b] text-white rounded-full px-6"
              >
                Continue Shopping
              </Button>
            </div>
          )}

          {items.map((item) => (
            <div
              key={item.product_id}
              data-testid={`cart-item-${item.product_id}`}
              className="flex gap-4 p-3 bg-white border border-[#e6e2d8] rounded-md"
            >
              <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-sm bg-[#efeadd]">
                {item.image_url ? (
                  <img
                    src={resolveImage(item.image_url)}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg text-[#1e392a] truncate">{item.name}</p>
                <p className="text-sm text-[#8a8d86]">{currency(item.price)}</p>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center border border-[#e6e2d8] rounded-full">
                    <button
                      onClick={() => updateQty(item.product_id, item.quantity - 1)}
                      data-testid={`cart-qty-dec-${item.product_id}`}
                      className="p-1.5 hover:bg-[#efeadd] rounded-l-full"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="px-3 text-sm">{item.quantity}</span>
                    <button
                      onClick={() => updateQty(item.product_id, item.quantity + 1)}
                      data-testid={`cart-qty-inc-${item.product_id}`}
                      className="p-1.5 hover:bg-[#efeadd] rounded-r-full"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(item.product_id)}
                    data-testid={`cart-remove-${item.product_id}`}
                    className="text-[#c36a4f] hover:text-[#a55840]"
                    aria-label="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <SheetFooter className="border-t border-[#e6e2d8] pt-6 flex-col">
            <div className="w-full flex items-center justify-between mb-4">
              <span className="text-sm text-[#8a8d86]">Subtotal</span>
              <span
                data-testid="cart-subtotal"
                className="font-serif text-2xl text-[#1e392a]"
              >
                {currency(subtotal)}
              </span>
            </div>
            <p className="text-xs text-[#8a8d86] mb-4">
              Free shipping on orders over ₹999. Taxes calculated at checkout.
            </p>
            <Button
              data-testid="cart-checkout-btn"
              onClick={() => {
                setIsOpen(false);
                navigate("/checkout");
              }}
              className="w-full bg-[#1e392a] hover:bg-[#14251b] text-white rounded-full h-12"
            >
              Checkout
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
